// ============================================
// 📱 APP: NVA (React App)
// 📄 FILE: src/components/home/DiscoverRail.tsx
// 🔧 PURPOSE: "Dintorni · Scopri la Puglia": le card con la foto che
//             scorrono in orizzontale fino al bordo, testo avorio su una
//             sfumatura espresso (leggibile su qualunque foto, in chiaro e in
//             scuro).
//
//             Le card cambiano con la vista:
//             - guest: Polignano, Mola, Alberobello (dalla sua pagina,
//               ottobre 2026);
//             - before: prima "Il tuo piano" (meteo, idee e budget per le
//               giornate), poi Esplora, Mola, Eventi, Locali partner;
//             - stay: Esplora, Eventi, Locali partner, Mola, Il tuo piano;
//             - after: come il soggiorno, senza il piano.
//             Eventi ha la sua foto (le luminarie di una festa, CC0), non
//             piu' il lungomare di Mola, che e' la citta'; Locali partner
//             quella di un locale partner (Van Westerhout).
// ============================================

import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '../../lib/utils';
import { FOCUS_RING, Section, SectionTitle } from './ui';
import { PLANNER_PATH } from './StayCard';

export type DiscoverVariant = 'guest' | 'before' | 'stay' | 'after';

type CardKey = 'polignano' | 'mola' | 'alberobello' | 'explore' | 'events' | 'partners' | 'planner';

interface DiscoverCard {
  to: string;
  image: string;
}

const CARDS: Record<CardKey, DiscoverCard> = {
  polignano: { to: '/cities/polignano-a-mare', image: '/images/cities/polignano/lama-monachile.jpg' },
  mola: { to: '/cities/mola-di-bari', image: '/images/cities/mola-di-bari/castello-angioino.jpg' },
  alberobello: { to: '/cities/alberobello', image: '/images/cities/explore/alberobello-trulli.jpg' },
  explore: { to: '/explore', image: '/images/cities/polignano/lama-monachile.jpg' },
  // La stessa foto della slide di un evento senza foto (HomeHeroCarousel).
  events: { to: '/events', image: '/images/events/luminarie.jpg' },
  partners: { to: '/partners', image: '/images/restaurants/van-westerhout.jpg' },
  planner: { to: PLANNER_PATH, image: '/images/cities/explore/alberobello-trulli.jpg' },
};

const ORDER: Record<DiscoverVariant, CardKey[]> = {
  guest: ['polignano', 'mola', 'alberobello'],
  before: ['planner', 'explore', 'mola', 'events', 'partners'],
  stay: ['explore', 'events', 'partners', 'mola', 'planner'],
  after: ['explore', 'events', 'partners', 'mola'],
};

export interface DiscoverRailProps {
  variant: DiscoverVariant;
  className?: string;
}

const DiscoverRail: React.FC<DiscoverRailProps> = ({ variant, className }) => {
  const { t } = useTranslation();
  const titleId = `home-discover-title-${variant}`;
  return (
    <Section labelledBy={titleId} flushRight className={className}>
      <SectionTitle id={titleId} eyebrow={t('home.discover.eyebrow')} title={t('home.discover.title')} className="pr-4" />
      <ul
        className="-my-1 flex snap-x snap-mandatory gap-2.5 overflow-x-auto py-1 pl-0.5 pr-4 scrollbar-hide"
        style={{ WebkitOverflowScrolling: 'touch', scrollPaddingLeft: 16 }}
      >
        {ORDER[variant].map((key) => {
          const card = CARDS[key];
          return (
            <li key={key} className="flex-shrink-0 snap-start">
              <Link
                to={card.to}
                className={cn(
                  'relative block h-[250px] w-[228px] overflow-hidden rounded-[18px] bg-nva-espresso motion-safe:transition-transform motion-safe:active:scale-[0.98]',
                  FOCUS_RING,
                )}
              >
                <img
                  src={card.image}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 h-full w-full object-cover"
                />
                <span
                  aria-hidden="true"
                  className="absolute inset-0"
                  style={{ background: 'linear-gradient(180deg, rgba(42,38,32,0) 38%, rgba(42,38,32,.88) 100%)' }}
                />
                <span className="absolute bottom-3.5 left-3.5 right-3.5 flex flex-col gap-[3px]">
                  <span className="text-[10.5px] font-extrabold uppercase tracking-eyebrow text-nva-gold-light">
                    {t(`home.discover.${key}.eyebrow`)}
                  </span>
                  <span className="font-display text-[21px] font-bold leading-tight text-nva-bone">
                    {t(`home.discover.${key}.title`)}
                  </span>
                  <span className="text-[13px] leading-snug text-nva-parchment">{t(`home.discover.${key}.sub`)}</span>
                </span>
              </Link>
            </li>
          );
        })}
      </ul>
    </Section>
  );
};

export default DiscoverRail;
